/**
 * Embed-mode utilities.
 *
 * When the site is loaded inside an <iframe> (e.g. from Wix), we want to:
 *   1. Detect that we're embedded so the UI can hide chrome that doesn't belong
 *      inside someone else's page (dev badges, cookie banner spam, etc).
 *   2. Continuously report the document's pixel height back to the parent
 *      window so the host (Wix) can resize the iframe and avoid the inner
 *      vertical scrollbar that ruins embedded experiences.
 *
 * The host listens for messages of the shape:
 *   { type: "lovable-embed:height", height: <number>, source: "dr-vahe" }
 *
 * The companion snippet served by the admin "Embed in Wix" page listens for
 * exactly that shape and resizes the iframe accordingly.
 */

const MESSAGE_SOURCE = "dr-vahe";
const MESSAGE_TYPE = "lovable-embed:height";

/** True when the page is rendered inside any iframe. */
export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access throws — which itself means we are framed.
    return true;
  }
};

/**
 * True when we should render in "embed" mode.
 *
 * Triggered by either:
 *   - the URL contains `?embed=1` (lets us preview the embed at /embed?embed=1
 *     in a normal browser tab), or
 *   - the page is loaded inside an iframe.
 */
export const isEmbedMode = (): boolean => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("embed") === "1") return true;
  return isInIframe();
};

/**
 * Start posting the document height to the parent window so the host iframe
 * can grow with the content. Returns a cleanup function that stops the loop.
 *
 * Uses a ResizeObserver on <body> for instant updates, plus a low-frequency
 * fallback poll for cases where the observer misses a layout (e.g. images
 * loading after a route transition).
 */
export const startHeightSync = (): (() => void) => {
  if (typeof window === "undefined") return () => {};

  let lastHeight = 0;
  const send = () => {
    const h = Math.max(
      document.documentElement.scrollHeight,
      document.body?.scrollHeight ?? 0,
    );
    if (h && Math.abs(h - lastHeight) > 1) {
      lastHeight = h;
      try {
        window.parent.postMessage(
          { type: MESSAGE_TYPE, height: h, source: MESSAGE_SOURCE },
          "*",
        );
      } catch {
        /* ignore — sandboxed iframes can't post */
      }
    }
  };

  // Initial nudge
  send();

  // Watch the body for any layout change.
  const ro = new ResizeObserver(() => send());
  if (document.body) ro.observe(document.body);

  // Fallback in case the observer misses something (e.g. font swap).
  const interval = window.setInterval(send, 750);

  // React to image loads, which often arrive after layout.
  const onLoad = () => send();
  window.addEventListener("load", onLoad);

  return () => {
    ro.disconnect();
    window.clearInterval(interval);
    window.removeEventListener("load", onLoad);
  };
};

/** Exported for the snippet generator so the host script stays in sync. */
export const EMBED_MESSAGE = { type: MESSAGE_TYPE, source: MESSAGE_SOURCE };
